/**
 * Giving up a balance — a credit, or a write-off.
 *
 * Two documents, and the dialog makes you say which. A credit says "you do not
 * owe this"; a write-off says "you owe it and we have stopped asking". They
 * land on different QuickBooks accounts (§F.4), they read differently in a
 * year-end review, and a single "clear the balance" button that silently
 * picked one would be a button that quietly misstates the year's revenue.
 *
 * Both need a reason, and the reason is the point: §6.9 puts a minimum role on
 * this because each one is money the company decided not to collect, and the
 * settings screen shows every one of them back with who did it and why.
 */
import { useEffect, useState } from 'react'
import { Modal } from './overlays.js'
import { Button, SectionLabel, Segmented } from './primitives.js'
import { Field, TextAreaField } from './fields.js'
import { formatDollars } from '../lib/format.js'
import { request } from '../lib/api.js'

export type SettleKind = 'credit' | 'write_off'

const WHAT: Record<SettleKind, { title: string; verb: string; says: string; account: string }> = {
  credit: {
    title: 'Credit this balance',
    verb: 'Credit',
    says: 'The customer does not owe it — we did not do the work, or we got it wrong.',
    account: 'Files in QuickBooks against customer credits.',
  },
  write_off: {
    title: 'Write this balance off',
    verb: 'Write off',
    says: 'The customer owes it and we have stopped asking.',
    account: 'Files in QuickBooks against bad debt.',
  },
}

export function SettleBalance({
  open, onClose, invoice, onDone,
}: {
  open: boolean
  onClose: () => void
  invoice: { id: string; reference: string; balance: number } | null
  onDone: (message: string) => void
}) {
  const [kind, setKind] = useState<SettleKind>('credit')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The whole balance, unless somebody types otherwise. That is what this is
  // for nine times out of ten, and pre-filling it is one less number to copy.
  useEffect(() => {
    if (!open || !invoice) return
    setKind('credit')
    setAmount(invoice.balance.toFixed(2))
    setReason('')
    setError(null)
  }, [open, invoice])

  if (!invoice) return null

  const asked = Number(amount)
  const ready = reason.trim() !== '' && Number.isFinite(asked) && asked > 0
    && asked <= invoice.balance + 0.005

  async function save() {
    if (!invoice) return
    setBusy(true)
    setError(null)
    try {
      await request(`/invoices/${invoice.id}/${kind === 'credit' ? 'credit' : 'write-off'}`, {
        method: 'POST',
        body: { amount: Number(amount), reason: reason.trim() },
      })
      onDone(`${WHAT[kind].verb} of ${formatDollars(Number(amount))} on ${invoice.reference}.`)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={WHAT[kind].title}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!ready || busy} onClick={() => void save()}>
            {busy ? 'Saving…' : WHAT[kind].verb}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error && (
          <p className="m-0 rounded-card border border-danger bg-danger-bg p-2 text-cell text-danger">
            {error}
          </p>
        )}

        <div>
          <SectionLabel className="mb-1.5 block">Which is it</SectionLabel>
          <Segmented
            label="Which is it"
            value={kind}
            onChange={(next) => setKind(next as SettleKind)}
            options={[
              { value: 'credit', label: 'Credit' },
              { value: 'write_off', label: 'Write off' },
            ]}
          />
          <p className="m-0 mt-1.5 text-sub text-text-muted">{WHAT[kind].says}</p>
          <p className="m-0 text-sub text-text-faint">{WHAT[kind].account}</p>
        </div>

        <Field
          label="Amount"
          value={amount}
          onChange={setAmount}
          required
          hint={`${invoice.reference} has ${formatDollars(invoice.balance)} outstanding.`}
        />

        <TextAreaField
          label="Reason"
          value={reason}
          onChange={setReason}
          placeholder={kind === 'credit'
            ? 'We missed two visits in June.'
            : 'Customer wound up; nothing left to collect from.'}
          hint="Recorded against the document, and shown with your name on the settings screen."
        />
      </div>
    </Modal>
  )
}
